import { SITE_PHOTOS } from '../sitePhotos';
import ConstructionPhotos from '../components/ConstructionPhotos';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSiteInfo } from '../context/SiteContext';
import Sidebar from '../components/Sidebar';
import { publicCollection } from '../lib/publicContent';
import { FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { cleanQuillHtml } from '../lib/imageCompression';

export default function CompanyProfile() {
  const { siteInfo } = useSiteInfo();
  const [pageContent, setPageContent] = useState<string>('');
  const heroImage = SITE_PHOTOS[0].url;
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const page = (await publicCollection('pages')).find(p=>p.id==='ho-so-nang-luc');
        if(page?.content) setPageContent(cleanQuillHtml(page.content));
      } catch (error) {
        console.error('Error fetching profile page content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPageContent();
  }, []);
  
  return (
    <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row gap-8">
      <Helmet>
        <title>Hồ sơ năng lực - {siteInfo.name}</title>
        <meta name="description" content={`Hồ sơ năng lực của ${siteInfo.name} - Đơn vị thi công khoan cọc nhồi, ép cừ và xử lý nền móng chuyên nghiệp.`} />
        <link rel="canonical" href={`${siteInfo.baseUrl}/ho-so-nang-luc`} />
      </Helmet>

      <div className="w-full md:w-3/4 min-w-0">
        <nav className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
          <a href="/" className="hover:text-primary whitespace-nowrap">Trang chủ</a>
          <span>/</span>
          <span className="text-gray-800 font-medium break-words max-w-full">Hồ sơ năng lực</span>
        </nav>

        <article className="bg-white p-6 md:p-8 rounded-lg shadow-sm border prose prose-sm md:prose-base max-w-none break-words">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pb-4 border-b-2 border-primary">
            <h1 className="text-2xl md:text-3xl font-bold text-primary m-0">
              Hồ sơ năng lực
            </h1>
            <Button 
              variant="primary"
              size="sm"
              asChild
            >
              <a 
                href="https://raw.githubusercontent.com/dinhkhanhtung/khoancocnhoi-website/main/pdf.pdf" 
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download size={18} /> Tải bản PDF
              </a>
            </Button>
          </div>

          {!loading && !pageContent && (
            <div className="profile-fallback">
              <div className="mb-8 rounded-lg overflow-hidden shadow-md border">
                <img src={heroImage} alt="Hồ sơ năng lực" className="w-full h-auto max-h-[550px] object-contain bg-gray-50" />
              </div>
              
              <div className="text-center mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</h2>
                <p className="font-medium">ĐỘC LẬP – TỰ DO – HẠNH PHÚC</p>
                <div className="w-24 h-0.5 bg-gray-300 mx-auto my-4"></div>
                <h3 className="text-lg md:text-xl font-bold text-primary">CÔNG TY TNHH PHÁT TRIỂN XÂY DỰNG NỀN MÓNG VIỆT NAM</h3>
                <p className="text-gray-600">MST: 0111144649</p>
              </div>

              <section className="mb-8">
                <h3 className="text-xl font-bold text-primary border-l-4 border-primary pl-3 mb-4">THÔNG ĐIỆP CỦA NỀN MÓNG VIỆT NAM</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Được thành lập vào năm 2025 thời điểm mà kinh tế đang dần phục hồi, tận dụng các lợi thế về chính sách kinh tế mở của Đảng và Nhà Nước, đặc biệt là lĩnh vực xây dựng, chúng tôi với đội ngũ cán bộ, nhân viên có nhiều năm kinh nghiệm đã tạo nên công ty TNHH Phát Triển Xây Dựng Nền Móng Việt Nam.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Lĩnh vực sản xuất của chúng tôi là thi công khoan cọc nhồi đường kính D300mm đến D1500. Với phương châm <strong>"Chữ tín đi đầu – Chất lượng tiên phong"</strong>, NỀN MÓNG VIỆT NAM đã có được sự tin tưởng tuyệt đối của khách hàng và đã gặt hái được nhiều thành tựu trong ngành.
                </p>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 italic text-blue-900">
                  "Rất mong được sự hợp tác của quý khách hàng. Xin chân thành cảm ơn!"
                </div>
              </section>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                <section>
                  <h3 className="text-lg font-bold text-primary mb-4 uppercase">I. Thông tin công ty</h3>
                  <ul className="space-y-2 text-sm md:text-base">
                    <li><strong>Tên Công Ty:</strong> CÔNG TY TNHH PHÁT TRIỂN XÂY DỰNG NỀN MÓNG VIỆT NAM</li>
                    <li><strong>Địa chỉ:</strong> Số 9F ngõ 77/47/28 Xuân La, Phường Tây Hồ, TP. Hà Nội</li>
                    <li><strong>Hotline:</strong> 0968 387 568</li>
                    <li><strong>Email:</strong> nenmongvietnam.kcn@gmail.com</li>
                    <li><strong>Tài khoản:</strong> 1140033022266 - Vietinbank (Thăng Long)</li>
                  </ul>
                </section>
                <section>
                  <h3 className="text-lg font-bold text-primary mb-4 uppercase">II. Lĩnh vực hoạt động</h3>
                  <ul className="list-disc pl-5 space-y-1 text-sm md:text-base text-gray-700">
                    <li>Khoan cọc nhồi D300 - D1500 cho cầu đường, nhà cao tầng, bờ kè.</li>
                    <li>Thi công khoan, giã đá địa chất cứng, sỏi sạn.</li>
                    <li>Thi công cạp tường vây, tường dẫn tầng hầm.</li>
                    <li>Gia cố nền móng, xử lý lún nghiêng.</li>
                  </ul>
                </section>
              </div>

              <section className="mb-10 overflow-x-auto">
                <h3 className="text-lg font-bold text-primary mb-4 uppercase">III. Năng lực máy móc thiết bị</h3>
                <table className="min-w-full border-collapse border border-gray-200 text-sm">
                  <thead>
                    <tr className="bg-primary text-white">
                      <th className="border border-gray-300 p-2 text-left">STT</th>
                      <th className="border border-gray-300 p-2 text-left">Tên thiết bị</th>
                      <th className="border border-gray-300 p-2 text-center">Số lượng</th>
                      <th className="border border-gray-300 p-2 text-left">Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr><td className="border border-gray-300 p-2">1</td><td className="border border-gray-300 p-2">Máy khoan Sany SR 285</td><td className="border border-gray-300 p-2 text-center">01</td><td className="border border-gray-300 p-2">Tài sản công ty</td></tr>
                    <tr className="bg-gray-50"><td className="border border-gray-300 p-2">2</td><td className="border border-gray-300 p-2">Máy Khoan Sany SR 155</td><td className="border border-gray-300 p-2 text-center">01</td><td className="border border-gray-300 p-2">Tài sản công ty</td></tr>
                    <tr><td className="border border-gray-300 p-2">3</td><td className="border border-gray-300 p-2">Máy Giã Đá CK1800 JK6</td><td className="border border-gray-300 p-2 text-center">01</td><td className="border border-gray-300 p-2">Tài sản công ty</td></tr>
                    <tr className="bg-gray-50"><td className="border border-gray-300 p-2">4</td><td className="border border-gray-300 p-2">Máy cẩu Hitachi KH 180 / KH 125</td><td className="border border-gray-300 p-2 text-center">02</td><td className="border border-gray-300 p-2">Tài sản công ty</td></tr>
                    <tr><td className="border border-gray-300 p-2">5</td><td className="border border-gray-300 p-2">Máy xúc Komatsu PC200-8</td><td className="border border-gray-300 p-2 text-center">01</td><td className="border border-gray-300 p-2">Tài sản công ty</td></tr>
                  </tbody>
                </table>
              </section>

              <section className="mb-10">
                <h3 className="text-lg font-bold text-primary mb-4 uppercase">IV. Công trình tiêu biểu</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded border-l-4 border-orange-500">
                    <span className="font-bold">Khu Sinh Thái Bảo Thọ Quốc Tế Hòa Bình</span>
                    <span className="text-primary font-bold">15.6 Tỷ VNĐ</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded border-l-4 border-blue-500">
                    <span className="font-bold">Vành Đai 4 – Cầu Đan Hoài (Hà Nội)</span>
                    <span className="text-primary font-bold">1.96 Tỷ VNĐ</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded border-l-4 border-green-500">
                    <span className="font-bold">Nhà Máy Risuntek Việt Nam (Hà Nam)</span>
                    <span className="text-primary font-bold">1.49 Tỷ VNĐ</span>
                  </div>
                </div>
              </section>

              <div className="text-center mt-12 pt-8 border-t">
                <p className="text-gray-500 text-sm italic">"Chữ tín đi đầu – Chất lượng tiên phong"</p>
              </div>
            </div>
          )}

          {pageContent && (
            <div className="profile-content">
              <div className="mb-8 rounded-lg overflow-hidden shadow-md border">
                <img src={heroImage} alt="Hồ sơ năng lực" className="w-full h-auto max-h-[550px] object-contain bg-gray-50" />
              </div>
              <div className="article-content ql-editor break-words" dangerouslySetInnerHTML={{ __html: cleanQuillHtml(pageContent) }} />
            </div>
          )}
          <ConstructionPhotos />
        </article>
      </div>
      
      <div className="w-full md:w-1/4 md:shrink-0">
        <Sidebar />
      </div>
    </div>
  );
}
